import { Check, LogOut, Sparkles } from "lucide-react";
import { useUserMe } from "@/lib/user-me";
import { useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/utils";

/**
 * Left rail of the onboarding shell.
 *
 *   ┌───────────────────────┐
 *   │  ▣  booqly            │   logo top
 *   │                       │
 *   │  ✦  Get started       │   Welcome marker (active on /welcome)
 *   │                       │
 *   │  1. About you         │   numbered form steps
 *   │  2. Your workspace    │
 *   │  3. Invite team       │
 *   │  4. Pick services     │
 *   │  5. Choose plan       │
 *   │                       │
 *   │  ✓  Done              │   Done marker (active on /done)
 *   │                       │
 *   │  ◯  liam@acme.test    │   user pill at bottom
 *   │     Sign out           │
 *   └───────────────────────┘
 *
 * The rail is purely presentational — it derives all of its state
 * (current step, completed steps) from the route + the StepInfo
 * array the shell passes in. Click on a completed numbered step
 * navigates back; upcoming + Welcome + Done are non-clickable from
 * a form step.
 */

export type RailStep = {
  key: string;
  label: string;
  subLabel: string;
};

export function OnboardingRail({
  steps,
  currentIndex,
  isWelcome,
  isDone,
  onJump,
}: {
  steps: RailStep[];
  /** Index into `steps` for the currently active form step.
   *  Ignored when isWelcome or isDone are true. */
  currentIndex: number;
  isWelcome: boolean;
  isDone: boolean;
  onJump: (idx: number) => void;
}) {
  const userMe = useUserMe();
  const clearAuth = useAuthStore((s) => s.clear);

  const handleSignOut = () => {
    clearAuth();
    window.location.href = "/";
  };

  const initial =
    userMe.data?.firstName?.trim()?.[0]?.toUpperCase() ??
    userMe.data?.email?.[0]?.toUpperCase() ??
    "?";

  return (
    <aside className="w-[280px] shrink-0 border-r border-border bg-background flex flex-col h-screen sticky top-0">
      {/* Logo */}
      <div className="px-6 py-6 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center text-sm font-bold">
          B
        </div>
        <span className="text-[15px] font-semibold tracking-tight text-foreground">
          Booqly
        </span>
      </div>

      {/* Step list */}
      <nav className="flex-1 px-3 overflow-y-auto">
        {/* Welcome milestone */}
        <RailMilestone
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="Get started"
          active={isWelcome}
          done={!isWelcome}
        />

        <div className="h-px bg-border my-3 mx-3" />

        {/* Numbered form steps */}
        <ol className="space-y-0.5">
          {steps.map((s, i) => {
            const done = !isWelcome && (isDone || i < currentIndex);
            const active = !isWelcome && !isDone && i === currentIndex;
            const upcoming = isWelcome || (!isDone && i > currentIndex);
            const clickable = done && !isDone;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => clickable && onJump(i)}
                  disabled={!clickable}
                  className={cn(
                    "w-full flex items-start gap-3 rounded-md px-3 py-2 text-left transition-colors",
                    active && "bg-muted",
                    clickable && "hover:bg-muted/60 cursor-pointer",
                    !clickable && "cursor-default",
                  )}
                  aria-current={active ? "step" : undefined}
                >
                  <span
                    className={cn(
                      "shrink-0 mt-0.5 h-5 w-5 rounded-full inline-flex items-center justify-center text-[10px] font-semibold border",
                      done && "bg-trend-positive text-white border-trend-positive",
                      active && "bg-primary text-primary-foreground border-primary",
                      upcoming && "border-border text-muted-foreground bg-card",
                    )}
                  >
                    {done ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        "block text-[13px] leading-tight",
                        active && "font-semibold text-foreground",
                        done && "font-medium text-foreground",
                        upcoming && "font-medium text-muted-foreground",
                      )}
                    >
                      {s.label}
                    </span>
                    <span className="block text-[11px] text-muted-foreground mt-0.5 leading-tight">
                      {s.subLabel}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="h-px bg-border my-3 mx-3" />

        {/* Done milestone */}
        <RailMilestone
          icon={<Check className="h-3.5 w-3.5" />}
          label="All set"
          active={isDone}
          done={false}
          muted={!isDone}
        />
      </nav>

      {/* User pill */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-primary-light text-primary inline-flex items-center justify-center text-xs font-semibold">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-medium text-foreground truncate">
              {userMe.data?.firstName ?? userMe.data?.email ?? "—"}
            </p>
            <button
              type="button"
              onClick={handleSignOut}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function RailMilestone({
  icon,
  label,
  active,
  done,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  done: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2",
        active && "bg-primary-light/40",
      )}
    >
      <span
        className={cn(
          "shrink-0 h-5 w-5 rounded-full inline-flex items-center justify-center",
          active && "bg-primary text-primary-foreground",
          done && !active && "bg-trend-positive text-white",
          muted && !active && !done && "bg-card text-muted-foreground border border-border",
          !active && !done && !muted && "bg-card text-muted-foreground border border-border",
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "text-[13px] leading-tight",
          active && "font-semibold text-foreground",
          !active && "font-medium text-muted-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}
